import { z } from "zod";

/**
 * The only place that reads `import.meta.env`.
 *
 * Validated at module load rather than at first use: a missing or malformed variable should
 * fail the moment the bundle evaluates, with the name of the variable, instead of surfacing
 * three screens later as `undefined` in a URL.
 */
const schema = z.object({
  VITE_API_BASE_URL: z.string().min(1).default("/api/v1"),
  // The default names the kit itself, not any one installation: a deployment that ships to a
  // customer sets VITE_APP_NAME in its own environment and never edits this line.
  VITE_APP_NAME: z.string().min(1).default("ALTEK ERP"),
  /**
   * The word-mark on the sign-in and invitation screens.
   *
   * SEPARATE FROM `VITE_APP_NAME` because they answer different questions. The app name is the
   * product ("ALTEK ERP"); this is WHOSE installation it is ("İNTRALİNE"), and it was hard-coded
   * in two components until now — so the second customer's sign-in screen would have greeted
   * them with the first customer's name. Both screens are seen BEFORE there is a session, so
   * neither can read the customer from the server; it has to come from the environment.
   */
  VITE_BRAND_NAME: z.string().min(1).default("ALTEK"),
  VITE_DEFAULT_LOCALE: z.enum(["tr-TR", "en-US"]).default("tr-TR"),
  VITE_SENTRY_DSN: z.string().default(""),
});

const parsed = schema.safeParse(import.meta.env);

if (!parsed.success) {
  const issues = parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`).join("\n");
  throw new Error(`Ortam değişkenleri geçersiz:\n${issues}`);
}

export const env = Object.freeze({
  apiBaseUrl: parsed.data.VITE_API_BASE_URL,
  appName: parsed.data.VITE_APP_NAME,
  brandName: parsed.data.VITE_BRAND_NAME,
  defaultLocale: parsed.data.VITE_DEFAULT_LOCALE,
  sentryDsn: parsed.data.VITE_SENTRY_DSN || null,
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
});

export type Env = typeof env;
